import { useEffect, useState } from "react";
import styles from "./UserDropdown.module.css";
import { CaretDownOutlined } from "@ant-design/icons";
import { HiOutlineBookOpen } from "react-icons/hi2";
import { FiActivity } from "react-icons/fi";
import { FaRegQuestionCircle } from "react-icons/fa";
import { LuLogOut } from "react-icons/lu";
import { MdLockOutline } from "react-icons/md";
import useComponentVisible from "./useComponentVisible";
import DefaultCompanyLogo from "../../../assets/img/Logo-Black-100px.png";
import ParentCompanyIcon from "../../../assets/img/parent-company-icon.png";
import Company from '../../../assets/img/change-org.png'
import {
  updateUserSSOStatus,
  userSignOut,
} from "../../../services/auth.service";
import { getCookie } from "../../../utils/cookies";
import Windows from "../../../assets/img/windows.png";
import { showToast } from "../../Toaster/Toaster";
import { OAuthProvider, getAuth, linkWithPopup, unlink } from "firebase/auth";
import { getUserImages } from "../../../services/common.service";
import OrganizationSwitcher from "../../OrganizationSwitcher/OrganizationSwitcher";
import { enableChristmasTheme, enableEasterTheme } from "../../../utils/chSeasonTheme";

const UserDropdown = () => {
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);
  const accessToken = getCookie("firebase_token") as string;
  const chUserDataCookie = localStorage.getItem("ch_user_data");
  const userData = JSON.parse(chUserDataCookie as string);
  const [currentUser, setCurrentUser] = useState<any>(userData);
  const auth = getAuth();
  const [companyLogo, setCompanyLogo] = useState<string>(DefaultCompanyLogo);
  const userCompanies = JSON.parse(localStorage.getItem("companies") as string);
  const selectedOrganization = userCompanies?.find((company: any) => company.isSelected);
  const [showOrganizationSwitcher, setShowOrganizationSwitcher] = useState(false);

  const toggleDropdown = () => {
    setIsComponentVisible(!isComponentVisible);
  };

  const onSignOut = () => {
    userSignOut();
  };

  const getUserImage = async (type: string, id: string) => {
    const response = await getUserImages(type, [id], accessToken);
    setCompanyLogo(response?.data[0]?.image?.imageSmall || DefaultCompanyLogo);
  };

  const connectMicrosoft = async () => {
    const provider = new OAuthProvider("microsoft.com");
    const user: any = auth.currentUser;

    linkWithPopup(user, provider)
      .then(async (newUser: any) => {
        if (
          newUser._tokenResponse.hasOwnProperty("email") &&
          newUser._tokenResponse.email?.toLowerCase() !==
            user.email.toLowerCase()
        ) {
          unlink(user, "microsoft.com").then(() => {});
          showToast(
            "Email linked with your account and your Microsoft account is different. Please use the same email account to connect.",
            { type: "error" }
          );
        } else {
          showToast(
            "Connected your microsoft account to your existing user account.",
            { type: "success" }
          );
          const response = await updateUserSSOStatus(
            accessToken,
            userData._id,
            true
          );
          if (response.data.success && response.data.data) {
            showToast("Successfully connected microsoft account.", {
              type: "success",
            });
            window.location.reload();
          }
        }
      })
      .catch(() => {
        showToast("Failed to connect account.", { type: "error" });
      });
  };

  const unlinkMicrosoft = async () => {
    try {
      const user: any = auth.currentUser;
      await unlink(user, "microsoft.com");

      const response = await updateUserSSOStatus(
        accessToken,
        userData._id,
        false
      );
      if (response.data.success && response.data.data) {
        showToast("Successfully disconnected microsoft account.", {
          type: "success",
        });
        window.location.reload();
      }
    } catch (err) {
      showToast("Failed to disconnect microsoft account.", { type: "error" });
    }
  };

  const goToLink = (url: string) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  useEffect(() => {
    getUserImage(
      userData?.provider?._id ? "provider" : "agency",
      userData?.provider?._id || userData?.agency?._id
    );
  });

  useEffect(() => {
    const fetchUserImages = async () => {
      if (userData && (userData?.provider?.parentCompany || userData?.agency?.parentCompany)) {
        const type = "parent-company";
        const ids = [userData?.provider?.parentCompany?._id || userData?.agency?.parentCompany?._id];
  
        try {
          const res = await getUserImages(type, ids, accessToken);
          if (res.data.length > 0) {
  
            if (userData.userGroup === 'Provider') {
              setCurrentUser({
                ...userData,
                provider: {
                  ...userData.provider,
                  parentCompany: {
                    ...userData.provider.parentCompany,
                    image: res.data[0].image
                  }
                }
              })
            } else {
              setCurrentUser({
                ...userData,
                agency: {
                  ...userData.agency,
                  parentCompany: {
                    ...userData.agency.parentCompany,
                    image: res.data[0].image
                  }
                }
              })
            }
          }
        } catch (error) {
          console.error('Error fetching user images:', error);
        }
      }
    };
  
    fetchUserImages();
  }, [ ]); 

  const handleChangeCompanyClick = () => {
    setShowOrganizationSwitcher(true);
  }

  return (
    <>
    {!showOrganizationSwitcher && (
      <div
      ref={ref}
      className={`${styles["user-dropdown"]} ${
        isComponentVisible ? styles["expanded"] : styles["collapse"]
      }`}
    >
      <div className={styles["user-info-wrapper"]}>
        <div style={enableChristmasTheme() ? { marginTop: 10, position: 'relative' } : { position: 'relative' }}>
        {enableChristmasTheme() && <img
            src={'/assets/images/hat.webp'}
            className={`${styles['christmas-hat']} ${isComponentVisible ? `${styles['christmas-hat-hidden']}` : ''}`}
            alt="User"
          />}
          {enableEasterTheme() && <img
            src={'/assets/images/easter-icon.png'}
            className={`${styles['easter-icon']} ${isComponentVisible ? `${styles['easter-icon-hidden']}` : ''}`}
            alt="User"
          />}
          <img
            src={currentUser?.provider?.parentCompany?.image?.imageSmall || currentUser?.agency?.parentCompany?.image?.imageSmall ? currentUser.provider?.parentCompany?.image?.imageSmall || currentUser.agency?.parentCompany?.image?.imageSmall : DefaultCompanyLogo}
            className={`${styles["user-image"]} ${styles["rounded-full"]} ${styles["border-2"]} ${styles["image-container"]}`}
            alt="User"
          />
        </div>

        <div className={styles["user-text"]}>
          <span className={styles["custom-text"]}>{userData?.fullName}</span>
          <br />
          <span style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '12px' }} className={styles["subtitle"]}>
            {
              (userData?.provider?.parentCompany || userData?.agency?.parentCompany) && (
                <img
                  src={ParentCompanyIcon}
                  style={{ width: '16px', height: '16px', marginRight: '4px' }}
                  alt="User"
                />
              )
            }

            {userData?.provider
              ? userData?.provider?.parentCompany?.name || userData?.provider?.companyName
              : userData?.agency
                ? userData?.agency?.parentCompany?.name || userData?.agency?.businessName
                : userData?.userGroup === "Admin" ? "Care Hires Super Admin": ""}
          </span>
        </div>
      </div>

      <div className={styles["expanded-content"]}>
        <ul className={styles["expanded-nav"]}>
        {(currentUser?.provider?.parentCompany || currentUser?.agency?.parentCompany) && (
          <li onClick={handleChangeCompanyClick}>
            <img src={Company} style={{marginLeft: '15px'}}/>
            <div>
            <span style={{margin: '0 0 0 15px'}}>Change Company</span>
            <p style={{fontSize: '10px', fontWeight: 500, margin: '0 0 0 15px'}}>{selectedOrganization.name}</p>
            </div>
          </li>
        )}
          {!(userData?.provider?.enforceSSO === true) && userData?.sso ? (
            <li  onClick={unlinkMicrosoft}>
              <img style={{marginLeft: '15px'}} src={Windows} alt="win" width="22" className={styles.icon} />
              <span className={styles["text-sm"]}>
                Disconnect Microsoft Account
              </span>
            </li>
          ) : !userData?.sso ? (
            <li  onClick={connectMicrosoft}>
              <img
                src={Windows}
                alt="win"
                width="22"
                style={{  marginLeft: "15px" }}
              />
              <span className={styles["text-sm"]}>
                Connect Microsoft Account
              </span>
            </li>
          ): null}
          <li
            onClick={() =>
              goToLink(import.meta.env.VITE_KNOWLEDGE_BASE_URL as string)
            }
          >
            <HiOutlineBookOpen style={{marginLeft: '15px'}} size={18} />
            <span className={styles["text-sm"]}> Knowledge Base</span>
          </li>
          <li
            onClick={() =>
              goToLink(import.meta.env.VITE_SYSTEM_UPTIME_STATUS_URL as string)
            }
          >
            <FiActivity style={{marginLeft: '15px'}} size={18} />
            <span className={styles["text-sm"]}>System Uptime Status</span>
          </li>
          <li
            onClick={() =>
              goToLink(import.meta.env.VITE_SUPPORT_DESK_URL as string)
            }
          >
            <FaRegQuestionCircle style={{marginLeft: '15px'}}  size={18} />
            <span className={styles["text-sm"]}>Support</span>
          </li>
          <li
            onClick={() =>
              goToLink(import.meta.env.VITE_PRIVACY_POLICY_URL as string)
            }
          >
            <MdLockOutline style={{marginLeft: '15px'}} size={18} />
            <span className={styles["text-sm"]}>Privacy Policy</span>
          </li>
          <li onClick={onSignOut}>
            <LuLogOut style={{marginLeft: '15px'}} size={18} />
            <span className={styles["text-sm"]}>Logout</span>
          </li>
        </ul>
      </div>

      <div className={styles["caret-wrapper"]} onClick={toggleDropdown}>
        <div className={styles["caret"]}>
          <CaretDownOutlined />
        </div>
      </div>
    </div>
    )}
    {showOrganizationSwitcher && (
      <div className="login-container">
        <OrganizationSwitcher
        show={showOrganizationSwitcher}
        userData={userData}
        userCompanies={userCompanies}
      />
      </div>
    )}
    </>
  );
};

export default UserDropdown;
